import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import type { OnboardingStep } from '@/pages/onboarding/components/stepStrip';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/**
 * `label`: the arrow and the target's name, everywhere but one place.
 * `iconOnly`: the arrow alone, for the Products bar on a phone.
 * `exit`: the in-shell File's Back, the one that leaves the import page:
 * an exit icon, "To Branches" and a hint beside it.
 */
export type StepBackButtonAppearance = 'label' | 'iconOnly' | 'exit';

type StepBackButtonCommonProps = {
    onPress: () => void;
    /** Shown disabled, never hidden (while a file is read, or an import runs). */
    isDisabled?: boolean;
    /**
     * Layout only (order, width), never the button's look. On `exit` it lays
     * out the button and its hint together.
     */
    className?: string;
};

export type StepBackButtonProps = StepBackButtonCommonProps &
    (
        | {
              appearance?: 'label' | 'iconOnly';
              /** The step Back opens; its name on the step strip is the label. */
              target: OnboardingStep;
          }
        | {
              /** Opens the branch list, which is not a step: no target. */
              appearance: 'exit';
              target?: never;
          }
    );

/**
 * The one Back of the import flow, on every step of the wizard and the
 * in-shell page: a ghost `Button` with a leading left arrow, labelled with
 * the step it opens as the step strip names it (`onboarding.steps.*`), so
 * Back and the strip always agree. A Back to Welcome reads "Back"
 * (`onboarding.frame.back`), never "Start". The icon-only form is named
 * "Back to <step>" (`onboarding.frame.backToStep`), as the strip's links
 * are; otherwise the visible label is the name.
 *
 * The `exit` form leaves the in-shell import page: an exit icon, "To
 * Branches" and the muted hint "Leaves the import page" beside it (under it
 * when the step stacks it), which describes the button to screen readers.
 *
 * Test IDs: `catalog-import-step-back`, and `catalog-import-step-back-hint`
 * on the exit hint. A step shows one Back.
 */
export const StepBackButton: React.FC<StepBackButtonProps> = ({
    onPress,
    isDisabled = false,
    className,
    ...variant
}) => {
    const { t } = useTranslation('pages');
    const hintId = React.useId();
    const arrow = <Icon aria-hidden icon="gravity-ui:arrow-left" />;

    if (variant.appearance === 'exit') {
        // Below sm a column: the button fills the width the step gives the
        // group (HeroUI's button is `w-fit`, so it does not stretch by
        // itself) and the hint sits under it. A row from sm.
        return (
            <div
                className={cn(
                    'flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3',
                    className,
                )}
            >
                <Button
                    aria-describedby={hintId}
                    className="w-full sm:w-auto"
                    isDisabled={isDisabled}
                    onPress={onPress}
                    variant="ghost"
                    {...getNodeTestIdProp('catalog-import-step-back')}
                >
                    <Icon
                        aria-hidden
                        icon="gravity-ui:arrow-right-from-square"
                    />
                    {t('catalogImport.upload.file.toBranches')}
                </Button>
                <Typography
                    color="muted"
                    id={hintId}
                    type="body-xs"
                    {...getNodeTestIdProp('catalog-import-step-back-hint')}
                >
                    {t('catalogImport.upload.file.leavesHint')}
                </Typography>
            </div>
        );
    }

    const stepName = t(`onboarding.steps.${variant.target}`);

    if (variant.appearance === 'iconOnly') {
        return (
            <Button
                aria-label={t('onboarding.frame.backToStep', {
                    step: stepName,
                })}
                className={className}
                isDisabled={isDisabled}
                isIconOnly
                onPress={onPress}
                variant="ghost"
                {...getNodeTestIdProp('catalog-import-step-back')}
            >
                {arrow}
            </Button>
        );
    }

    return (
        <Button
            className={className}
            isDisabled={isDisabled}
            onPress={onPress}
            variant="ghost"
            {...getNodeTestIdProp('catalog-import-step-back')}
        >
            {arrow}
            {variant.target === 'start' ? t('onboarding.frame.back') : stepName}
        </Button>
    );
};
